/**
 * Shared DMS (degrees-minutes-seconds) coordinate parsing utilities.
 *
 * Originally extracted from packages/contracts/src/import/validate-row.ts so
 * the same logic can be reused by the map-search coordinate parser without
 * creating a dependency on the import module.
 */

/** Matches a single DMS coordinate value (one axis), with optional direction. */
export const DMS_COORDINATE_RE =
  /^\s*([NSEW])?\s*([+-]?\d+(?:\.\d+)?)\s*°\s*(\d+(?:\.\d+)?)\s*['′]\s*(\d+(?:\.\d+)?)\s*(?:["″])?\s*([NSEW])?\s*$/i;

/** Matches a bare decimal coordinate (no DMS markers). */
export const DECIMAL_COORDINATE_RE = /^[+-]?\d+(?:\.\d+)?$/;

/** Formats a decimal coordinate value with up to 7 decimal places. */
export function formatCoordinate(value: number): string {
  return String(Number(value.toFixed(7)));
}

/**
 * Resolves a DMS direction from an optional prefix and suffix direction letter.
 * Returns null if both are present but conflict. Returns empty string when absent.
 */
export function resolveDmsDirection(
  prefixDirection: string | undefined,
  suffixDirection: string | undefined,
): string | null {
  if (
    prefixDirection &&
    suffixDirection &&
    prefixDirection.toUpperCase() !== suffixDirection.toUpperCase()
  ) {
    return null;
  }
  return (prefixDirection ?? suffixDirection)?.toUpperCase() ?? '';
}

/** Validates that DMS parts are in range (minutes < 60, seconds < 60). */
export function isValidDmsParts(degrees: number, minutes: number, seconds: number): boolean {
  const values = [degrees, minutes, seconds];
  return (
    values.every((coordinatePart) => Number.isFinite(coordinatePart)) &&
    minutes >= 0 &&
    minutes < 60 &&
    seconds >= 0 &&
    seconds < 60
  );
}

/**
 * Parses a single DMS coordinate string (one axis) into a decimal string.
 * Returns null when the value does not match the DMS pattern or has invalid parts.
 *
 * Examples: "52°13'46\"N", "N52°13'46\"", "16°07'24\"E"
 */
export function parseDmsCoordinate(value: string): string | null {
  const match = DMS_COORDINATE_RE.exec(value);
  if (!match) return null;

  const [, prefixDirection, degreesRaw, minutesRaw, secondsRaw, suffixDirection] = match;
  const direction = resolveDmsDirection(prefixDirection, suffixDirection);
  if (direction === null) return null;

  const degrees = Number(degreesRaw);
  const minutes = Number(minutesRaw);
  const seconds = Number(secondsRaw);
  if (!isValidDmsParts(degrees, minutes, seconds)) return null;

  const sign = degrees < 0 || direction === 'S' || direction === 'W' ? -1 : 1;
  const decimal = Math.abs(degrees) + minutes / 60 + seconds / 3600;
  return formatCoordinate(sign * decimal);
}

/**
 * Parses a single coordinate value that may be either a decimal or a DMS string.
 * Returns the decimal string representation, or null if parsing fails.
 *
 * Used by the xlsx import row validator. For map coordinate pair parsing,
 * see `parseDmsPair` in the web feature layer.
 */
export function parseCoordinateInput(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (DECIMAL_COORDINATE_RE.test(trimmed)) return formatCoordinate(Number(trimmed));
  return parseDmsCoordinate(trimmed);
}
